import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Layout } from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import airportData from "../data/airports.json";

// Airport code to city name lookup
function getAirportName(code) {
  if (!code) return "";
  return airportData[code] || code;
}
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Search,
  RefreshCw,
  PlaneLanding,
  PlaneTakeoff,
  ChevronLeft,
  ChevronRight,
  Clock,
  Plane,
  ArrowRight,
  Download,
} from "lucide-react";
import { api } from "../lib/utils";
import { toast } from "sonner";

function getAirlineLogo(code) {
  if (!code) return null;
  return `https://images.kiwi.com/airlines/64/${code.trim()}.png`;
}

function formatStadTime(stadStr) {
  if (!stadStr || stadStr.length < 12) return "";
  return `${stadStr.substring(8, 10)}:${stadStr.substring(10, 12)}`;
}

function formatStadDate(stadStr) {
  if (!stadStr || stadStr.length < 8) return "";
  return `${stadStr.substring(6, 8)}/${stadStr.substring(4, 6)}`;
}

function formatStadDay(stadStr) {
  if (!stadStr || stadStr.length < 8) return "";
  const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  const dt = new Date(parseInt(stadStr.substring(0, 4)), parseInt(stadStr.substring(4, 6)) - 1, parseInt(stadStr.substring(6, 8)));
  return days[dt.getDay()];
}

function getStatusDisplay(status) {
  const map = {
    SCHEDULED: { label: "Scheduled", cls: "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300" },
    DEPARTED: { label: "Flight Depart", cls: "bg-blue-500 text-white" },
    ARRIVED: { label: "Flight Arrived", cls: "bg-green-500 text-white" },
    LANDED: { label: "Flight Landed", cls: "bg-green-500 text-white" },
    DELAYED: { label: "Flight Delayed", cls: "bg-red-500 text-white" },
    CANCELLED: { label: "Cancelled", cls: "bg-red-600 text-white" },
    BOARDING: { label: "Boarding", cls: "bg-purple-500 text-white" },
    "FINAL CALL": { label: "Final Call", cls: "bg-orange-500 text-white" },
    "GATE OPEN": { label: "Gate Open", cls: "bg-cyan-500 text-white" },
    "GATE CLOSED": { label: "Gate Closed", cls: "bg-amber-600 text-white" },
    "GATE SHOWN": { label: "Gate Shown", cls: "bg-teal-500 text-white" },
    "EN ROUTE": { label: "En Route", cls: "bg-sky-500 text-white" },
    "CAROUSEL OPEN": { label: "Carousel Open", cls: "bg-emerald-500 text-white" },
    "FIRST BAG": { label: "First Bag", cls: "bg-green-600 text-white" },
    "CAROUSEL CLOSED": { label: "Carousel Closed", cls: "bg-zinc-500 text-white" },
    "RETURN TO BAY": { label: "Return to Bay", cls: "bg-amber-500 text-white" },
    DIVERTED: { label: "Diverted", cls: "bg-red-400 text-white" },
  };
  const s = map[status] || map.SCHEDULED;
  return <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${s.cls}`}>{s.label}</span>;
}

function TimeVisual({ scheduled, estimated, actual }) {
  const schedTime = formatStadTime(scheduled);
  const day = formatStadDay(scheduled);
  const updatedTime = actual ? formatStadTime(actual) : estimated ? formatStadTime(estimated) : "";
  const hasUpdate = updatedTime && updatedTime !== schedTime;
  return (
    <div className="flex flex-col items-center min-w-[110px]">
      {hasUpdate ? (
        <>
          <span className="text-xs text-muted-foreground line-through">{day} {schedTime}</span>
          <div className="flex items-center gap-1 my-0.5"><div className="w-5 h-px bg-muted-foreground/40" /><Plane className="h-3 w-3 text-muted-foreground rotate-90" /><div className="w-5 h-px bg-muted-foreground/40" /></div>
          <span className="text-sm font-semibold">{day} {updatedTime}</span>
        </>
      ) : (
        <><span className="text-sm font-semibold">{day} {schedTime}</span><div className="flex items-center gap-1 my-0.5"><div className="w-5 h-px bg-muted-foreground/40" /><Plane className="h-3 w-3 text-muted-foreground rotate-90" /><div className="w-5 h-px bg-muted-foreground/40" /></div></>
      )}
    </div>
  );
}

function AirlineLogoImg({ code }) {
  const url = getAirlineLogo(code);
  if (!url) return <div className="w-8 h-8 rounded bg-muted flex items-center justify-center text-[9px] font-bold text-muted-foreground">{code}</div>;
  return (
    <div className="relative w-8 h-8 shrink-0">
      <img src={url} alt={code} className="w-8 h-8 object-contain rounded" onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
      <div className="w-8 h-8 rounded bg-muted items-center justify-center text-[9px] font-bold text-muted-foreground hidden">{code}</div>
    </div>
  );
}

function FlightRow({ flight, isArrival }) {
  const codeshare = flight.codeshare || "";
  return (
    <div className="grid items-center px-3 py-2 border-b border-border hover:bg-muted/30 transition-colors text-xs"
         style={{ gridTemplateColumns: '45px 190px 110px minmax(140px,1fr) 55px 70px 140px 65px 105px 16px' }}>
      <div className="text-center font-mono text-muted-foreground">{formatStadDate(flight.scheduled_time)}</div>
      <div className="flex items-center gap-2 min-w-0">
        <AirlineLogoImg code={flight.airline_code} />
        <div className="min-w-0">
          <div className="text-[10px] text-muted-foreground font-medium uppercase truncate">{isArrival ? `${getAirportName(flight.origin)}(${flight.origin})` : "KUALA LUMPUR(KUL)"}</div>
          <div className="font-semibold text-sm">{flight.flight_no || "---"}</div>
        </div>
      </div>
      <div className="flex justify-center"><TimeVisual scheduled={flight.scheduled_time} estimated={flight.estimated_time} actual={flight.actual_time} /></div>
      <div className="min-w-0">
        <div className="font-medium text-sm truncate">{isArrival ? "Kuala Lumpur(KUL)" : `${getAirportName(flight.destination)}(${flight.destination})`}</div>
        <div className="text-[10px] text-muted-foreground truncate">{codeshare ? `CS: ${codeshare}` : ""}</div>
      </div>
      <div className="text-center font-mono">{flight.aircraft_type || "---"}</div>
      <div className="text-center font-mono">{flight.callsign || "---"}</div>
      <div className="text-center font-mono truncate" title={isArrival ? (flight.baggage_claim || "") : (flight.checkin_counter || "")}>{isArrival ? (flight.baggage_claim || "---") : (flight.checkin_counter || "---")}</div>
      <div className="text-center font-mono">{flight.boarding_gate || flight.gate_code || flight.stand_code || "---"}</div>
      <div className="flex justify-center">{getStatusDisplay(flight.status)}</div>
      <ArrowRight className="h-3 w-3 text-orange-400 hidden lg:block" />
    </div>
  );
}

function FlightListHeader({ isArrival }) {
  return (
    <div className="grid items-center px-3 py-1.5 border-b border-border bg-muted/40 text-[10px] text-muted-foreground font-semibold uppercase"
         style={{ gridTemplateColumns: '45px 190px 110px minmax(140px,1fr) 55px 70px 140px 65px 105px 16px' }}>
      <div className="text-center">Date</div>
      <div>Flight</div>
      <div className="text-center">Schedule</div>
      <div>{isArrival ? "Origin" : "Destination"}</div>
      <div className="text-center">A/C</div>
      <div className="text-center">Regn</div>
      <div className="text-center">{isArrival ? "Baggage" : "Check-in"}</div>
      <div className="text-center">Gate</div>
      <div className="text-center">Status</div>
      <div></div>
    </div>
  );
}

function FlightList({ flights, leg, loading }) {
  if (loading) return <div className="text-center py-12"><RefreshCw className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /><p className="text-muted-foreground mt-2 text-sm">Loading flights...</p></div>;
  if (!flights || flights.length === 0) return <div className="text-center py-12"><Plane className="h-8 w-8 mx-auto text-muted-foreground mb-2" /><p className="text-muted-foreground font-medium text-sm">No flights found</p></div>;
  const isArrival = leg === "A";
  return (
    <div>
      <FlightListHeader isArrival={isArrival} />
      {flights.map((flight, idx) => (
        <FlightRow key={`${flight.flight_no}-${flight.leg}-${flight.scheduled_time}-${idx}`} flight={flight} isArrival={isArrival} />
      ))}
    </div>
  );
}

// Pagination component with all page numbers
function PageSelector({ page, totalPages, onPageChange, total, limit }) {
  if (totalPages <= 1) return null;

  // Build page number array with ellipsis for very large page counts
  const getPageNumbers = () => {
    if (totalPages <= 15) {
      return Array.from({ length: totalPages }, (_, i) => i);
    }
    // Show: 1 2 3 ... (page-1) page (page+1) ... (last-2) (last-1) last
    const pages = new Set();
    for (let i = 0; i < Math.min(3, totalPages); i++) pages.add(i);
    for (let i = Math.max(0, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.add(i);
    for (let i = Math.max(0, totalPages - 3); i < totalPages; i++) pages.add(i);
    return Array.from(pages).sort((a, b) => a - b);
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/20">
      <p className="text-xs text-muted-foreground shrink-0">
        {page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total} flights
      </p>
      <div className="flex items-center gap-0.5 flex-wrap justify-end">
        <Button variant="outline" size="sm" className="h-6 w-6 p-0 text-[10px]" onClick={() => onPageChange(Math.max(0, page - 1))} disabled={page === 0}>
          <ChevronLeft className="h-3 w-3" />
        </Button>
        {pageNumbers.map((p, idx) => {
          // Check for ellipsis
          const showEllipsis = idx > 0 && p - pageNumbers[idx - 1] > 1;
          return (
            <span key={p} className="flex items-center">
              {showEllipsis && <span className="text-[10px] text-muted-foreground px-0.5">…</span>}
              <Button
                variant={p === page ? "default" : "outline"}
                size="sm"
                className={`h-6 min-w-[24px] px-1.5 text-[10px] ${p === page ? "font-bold" : ""}`}
                onClick={() => onPageChange(p)}
              >
                {p + 1}
              </Button>
            </span>
          );
        })}
        <Button variant="outline" size="sm" className="h-6 w-6 p-0 text-[10px]" onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}>
          <ChevronRight className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

export default function FidsPage({ terminal, terminalName, terminalLabel }) {
  const [activeLeg, setActiveLeg] = useState("D");
  const [allFlights, setAllFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [gateFilter, setGateFilter] = useState("");
  const isLive = !dateFrom && !dateTo;
  const limit = 50;

  const todayStr = useMemo(() => {
    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Kuala_Lumpur", year: "numeric", month: "2-digit", day: "2-digit",
    });
    return formatter.format(new Date()).replace(/-/g, "");
  }, []);

  const datesToQuery = useMemo(() => {
    const from = dateFrom || todayStr;
    const to = dateTo || from;
    const dates = [];
    let d = from;
    for (let i = 0; i < 7 && d <= to; i++) {
      dates.push(d);
      const dt = new Date(parseInt(d.substring(0,4)), parseInt(d.substring(4,6))-1, parseInt(d.substring(6,8)));
      dt.setDate(dt.getDate() + 1);
      d = `${dt.getFullYear()}${String(dt.getMonth()+1).padStart(2,"0")}${String(dt.getDate()).padStart(2,"0")}`;
    }
    return dates;
  }, [dateFrom, dateTo, todayStr]);

  const displayDateRange = useMemo(() => {
    const fmt = (d) => d.length === 8 ? `${d.substring(6,8)}/${d.substring(4,6)}/${d.substring(0,4)}` : d;
    if (datesToQuery.length === 1) return fmt(datesToQuery[0]);
    return `${fmt(datesToQuery[0])} \u2014 ${fmt(datesToQuery[datesToQuery.length-1])}`;
  }, [datesToQuery]);

  const fetchFlights = useCallback(async (isRefresh = false, jumpToNow = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const allResults = [];
      let suggested = 0;
      for (let i = 0; i < datesToQuery.length; i++) {
        const params = new URLSearchParams({ terminal, leg: activeLeg, date: datesToQuery[i], limit: '1000', skip: '0', page_size: String(limit) });
        if (search) params.append("search", search);
        const res = await api.get(`/api/fids?${params}`);
        allResults.push(...(res.data.flights || []));
        if (i === 0) suggested = res.data.suggested_page || 0;
      }
      allResults.sort((a, b) => (a.scheduled_time || '').localeCompare(b.scheduled_time || ''));
      setAllFlights(allResults);
      if (jumpToNow && !search) {
        const maxPage = Math.max(0, Math.ceil(allResults.length / limit) - 1);
        setPage(Math.min(suggested, maxPage));
      }
    } catch (error) {
      console.error("Failed to fetch FIDS data:", error);
      toast.error("Failed to load flight data");
    } finally { setLoading(false); setRefreshing(false); }
  }, [terminal, activeLeg, datesToQuery, search]);

  const filteredFlights = useMemo(() => {
    if (!gateFilter) return allFlights;
    return allFlights.filter(f => {
      const gate = (f.boarding_gate || f.gate_code || f.stand_code || '').toUpperCase();
      return gate.startsWith(gateFilter);
    });
  }, [allFlights, gateFilter]);

  const total = filteredFlights.length;
  const totalPages = Math.ceil(total / limit);
  const paginatedFlights = filteredFlights.slice(page * limit, (page + 1) * limit);

  const gateLetters = useMemo(() => {
    const letters = new Set();
    allFlights.forEach(f => {
      const gate = (f.boarding_gate || f.gate_code || f.stand_code || '').toUpperCase();
      if (gate && /^[A-Z]/.test(gate)) letters.add(gate[0]);
    });
    return Array.from(letters).sort();
  }, [allFlights]);

  // One round-trip per date: the first response carries suggested_page so the board opens at "now"
  useEffect(() => { fetchFlights(false, true); }, [fetchFlights]);
  useEffect(() => { setPage(0); }, [search, gateFilter]);

  const handleSearch = (e) => { e.preventDefault(); setSearch(searchInput); setPage(0); };
  const clearSearch = () => { setSearchInput(""); setSearch(""); setPage(0); };
  const goLive = () => { setDateFrom(""); setDateTo(""); setGateFilter(""); setSearch(""); setSearchInput(""); setPage(0); };

  const handleExport = useCallback(async () => {
    const XLSX = (await import("xlsx")).default || await import("xlsx");
    const exportData = filteredFlights.map(f => ({
      "Flight": f.flight_no || "",
      "Schedule": f.scheduled_time ? `${f.scheduled_time.substring(6,8)}/${f.scheduled_time.substring(4,6)} ${f.scheduled_time.substring(8,10)}:${f.scheduled_time.substring(10,12)}` : "",
      "Origin": f.origin || "KUL",
      "Destination": f.destination || "KUL",
      "Dest Name": f.destination ? (airportData[f.destination] || f.destination) : "",
      "A/C Type": f.aircraft_type || "",
      "Regn No": f.callsign || "",
      "Check-in": f.checkin_counter || "",
      "Gate": f.boarding_gate || f.gate_code || "",
      "Status": f.status || "",
      "Codeshare": f.codeshare || "",
      "Baggage": f.baggage_claim || "",
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `${terminalLabel} ${activeLeg === "D" ? "Dep" : "Arr"}`);
    XLSX.writeFile(wb, `FIDS_${terminalLabel.replace(/\s/g,"_")}_${activeLeg === "D" ? "Departures" : "Arrivals"}_${datesToQuery[0]}.xlsx`);
  }, [filteredFlights, activeLeg, terminalLabel, datesToQuery]);

  return (
    <Layout>
      <div className="w-full px-3 py-3 max-w-[1920px] mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
          <div>
            <h1 className="font-heading text-xl md:text-2xl font-bold tracking-tight flex items-center gap-2">
              {terminalLabel === "FIDS T1" && <PlaneTakeoff className="h-5 w-5 text-sky-500" />}
              {terminalLabel === "FIDS T2" && <PlaneTakeoff className="h-5 w-5 text-emerald-500" />}
              {terminalLabel === "FIDS Cargo" && <Plane className="h-5 w-5 text-amber-500" />}
              {terminalLabel}
            </h1>
            <p className="text-muted-foreground text-xs">{terminalName}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded"><Clock className="h-3 w-3" />{displayDateRange}</div>
            <Button variant="outline" size="sm" onClick={() => fetchFlights(true)} disabled={refreshing} className="h-7 text-xs"><RefreshCw className={`h-3 w-3 mr-1 ${refreshing ? "animate-spin" : ""}`} />Refresh</Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          {/* LIVE button */}
          <button onClick={goLive} className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold transition-all ${isLive ? "bg-green-500 text-white shadow-sm shadow-green-500/30" : "bg-muted text-muted-foreground hover:bg-green-500/20 hover:text-green-600 border border-border"}`}>
            <span className={`inline-block w-2 h-2 rounded-full ${isLive ? "bg-white animate-pulse" : "bg-green-500"}`} />
            LIVE
          </button>
          <div className="h-5 w-px bg-border hidden sm:block" />
          {/* Date range */}
          <div className="flex items-center gap-1 text-xs">
            <span className="text-muted-foreground">From:</span>
            <input type="date" value={dateFrom ? `${dateFrom.substring(0,4)}-${dateFrom.substring(4,6)}-${dateFrom.substring(6,8)}` : ""} onChange={(e) => setDateFrom(e.target.value ? e.target.value.replace(/-/g,"") : "")} className="text-xs bg-background border border-border rounded px-2 py-1 h-7" />
            <span className="text-muted-foreground">To:</span>
            <input type="date" value={dateTo ? `${dateTo.substring(0,4)}-${dateTo.substring(4,6)}-${dateTo.substring(6,8)}` : ""} onChange={(e) => setDateTo(e.target.value ? e.target.value.replace(/-/g,"") : "")} className="text-xs bg-background border border-border rounded px-2 py-1 h-7" />
          </div>
          <div className="h-5 w-px bg-border hidden sm:block" />
          <div className="flex items-center gap-1 text-xs">
            <span className="text-muted-foreground">Gate:</span>
            <select value={gateFilter} onChange={(e) => setGateFilter(e.target.value)} className="text-xs bg-background border border-border rounded px-2 py-1 h-7">
              <option value="">All Gates</option>
              {gateLetters.map(l => <option key={l} value={l}>Gate {l}</option>)}
            </select>
          </div>
          <div className="h-5 w-px bg-border hidden sm:block" />
          <form onSubmit={handleSearch} className="flex gap-1">
            <div className="relative"><Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" /><Input placeholder="Search..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)} className="pl-7 bg-background h-7 text-xs w-36" /></div>
            <Button type="submit" size="sm" className="h-7 text-xs px-2">Go</Button>
            {search && <Button type="button" variant="outline" size="sm" onClick={clearSearch} className="h-7 text-xs px-2">{"\u2715"}</Button>}
          </form>
          <div className="h-5 w-px bg-border hidden sm:block" />
          <Button variant="outline" size="sm" onClick={handleExport} className="h-7 text-xs gap-1" disabled={total === 0}>
            <Download className="h-3 w-3" />Export
          </Button>
        </div>

        <Tabs value={activeLeg} onValueChange={setActiveLeg} className="w-full">
          <TabsList className="mb-2 h-8">
            <TabsTrigger value="D" className="gap-1.5 text-xs h-7"><PlaneTakeoff className="h-3.5 w-3.5" />Departures{activeLeg === "D" && total > 0 && <Badge variant="secondary" className="ml-1 text-[10px] px-1 py-0">{total}</Badge>}</TabsTrigger>
            <TabsTrigger value="A" className="gap-1.5 text-xs h-7"><PlaneLanding className="h-3.5 w-3.5" />Arrivals{activeLeg === "A" && total > 0 && <Badge variant="secondary" className="ml-1 text-[10px] px-1 py-0">{total}</Badge>}</TabsTrigger>
          </TabsList>
          <Card className="bg-card/50 border-border overflow-hidden">
            <CardContent className="p-0">
              <PageSelector page={page} totalPages={totalPages} onPageChange={setPage} total={total} limit={limit} />
              <TabsContent value="D" className="mt-0"><FlightList flights={paginatedFlights} leg="D" loading={loading} /></TabsContent>
              <TabsContent value="A" className="mt-0"><FlightList flights={paginatedFlights} leg="A" loading={loading} /></TabsContent>
            </CardContent>
          </Card>
        </Tabs>
      </div>
    </Layout>
  );
}
